"use client";
import React, { useState, useEffect } from "react";
import { Marker } from "react-map-gl/mapbox";
import MapBoxMapWrapper from "@/components/shared/MapBoxMapWrapper";
import AmenityMapIcon from "@/components/shared/AmenityMapIcon";
import UralityAvatarImage from "@/components/shared/UralityAvatarImage";
import EventParticipantGroupParticipantView from "@/components/eventmanagement/EventParticipantGroupParticipantView";
import "@/components/events/EventKioskMapView.css";
import useUtils from "@/components/shared/useUtils";
import UralityImage from "../shared/UralityImage";

const EventKioskMapView = ({
  name,
  event,
  eventMap,
  managedEventParticipantGroups,
  allParticipants,
  participantPoints,
  points,
  amenityPoints,
  mapRoutes
}) => {
  const [pointList, setPointList] = useState([]);
  const [previewPoint, setPreviewPoint] = useState(null);
  const [selectedPoint, setSelectedPoint] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [kioskMarkers, setKioskMarkers] = useState([]);
  const [amenityOverlays, setAmenityOverlays] = useState(null);
  const [defaultCenter, setDefaultCenter] = useState(null);
  const utils = useUtils();

  const markerId = pt => pt.participant?.resourceName ?? pt.id;

  const truncateText = (text, maxLength = 255) => {
    if (!text) return null;
    return text.length <= maxLength
      ? text
      : text.slice(0, maxLength).trim() + "...";
  };

  // ── Fit bounds ──────────────────────────────────────────────
  useEffect(() => {
    const allLocations = [];
    if (participantPoints) {
      Object.keys(participantPoints).forEach(key => {
        participantPoints[key].forEach(pt => {
          if (pt.location) allLocations.push(pt.location);
        });
      });
    }
    if (points) {
      points.forEach(pt => {
        if (pt.location) allLocations.push(pt.location);
      });
    }
    if (allLocations.length > 0) {
      setDefaultCenter(
        utils.createFitBounds(allLocations, {
          top: 60,
          bottom: 60,
          left: 40,
          right: 40
        })
      );
    }
  }, [participantPoints, points]);

  // ── Flatten points for prev/next navigation ────────────────────
  useEffect(() => {
    if (!participantPoints) return;
    const flat = [];
    Object.keys(participantPoints).forEach(key => {
      participantPoints[key].forEach(pt => {
        if (pt.location) flat.push(pt);
      });
    });
    setPointList(flat);
  }, [participantPoints]);

  // ── Build avatar markers ─────────────────────────────────────
  useEffect(() => {
    if (!participantPoints) return;

    const newMarkers = [];
    Object.keys(participantPoints).forEach(key => {
      participantPoints[key].forEach(pt => {
        if (!pt.location) return;
        const profile = pt.participant;

        newMarkers.push(
          <Marker
            key={markerId(pt)}
            longitude={pt.location.longitude}
            latitude={pt.location.latitude}
            anchor="center"
            onClick={e => {
              e.originalEvent?.stopPropagation();
              const lng = pt.location.longitude;
              const lat = pt.location.latitude;
              setPreviewPoint(pt);
              setSelectedLocation({ id: markerId(pt), longitude: lng, latitude: lat });
            }}
          >
            <div className="kiosk-map-marker">
              <UralityAvatarImage
                imageId={profile?.logoId ?? null}
                name={profile?.displayName ?? null}
                type={"BUSINESS"}
                size="sm"
                outline={true}
              />
            </div>
          </Marker>
        );
      });
    });

    setKioskMarkers(newMarkers);
  }, [participantPoints]);

  // ── Amenity overlays ─────────────────────────────────────────
  useEffect(() => {
    if (amenityPoints && amenityPoints.length > 0) {
      setAmenityOverlays(
        amenityPoints.map(pt => (
          <AmenityMapIcon key={pt.id} amenityPoint={pt} />
        ))
      );
    }
  }, [amenityPoints]);

  const navigatePinPreview = direction => {
    const activePoint = previewPoint;
    if (!activePoint || !pointList.length) return;
    const idx = pointList.findIndex(p => p.id === activePoint.id);
    const nextIdx =
      direction === "prev"
        ? idx <= 0
          ? pointList.length - 1
          : idx - 1
        : idx >= pointList.length - 1
        ? 0
        : idx + 1;
    const nextPoint = pointList[nextIdx];

    setPreviewPoint(nextPoint);
    if (selectedPoint) setSelectedPoint(nextPoint);
    setSelectedLocation({
      id: markerId(nextPoint),
      longitude: nextPoint.location.longitude,
      latitude: nextPoint.location.latitude
    });
  };

  const onPrevPoint = () => navigatePinPreview("prev");
  const onNextPoint = () => navigatePinPreview("next");

  const renderPinPreviewBar = () => {
    if (!previewPoint) return null;
    const profile = previewPoint.participant;
    const title = profile?.displayName;
    const description = truncateText(profile?.description, 255);

    return (
      <div className="kiosk-pin-preview-shell is-visible">
        <div
          className="kiosk-pin-preview-card"
          onClick={() => setSelectedPoint(previewPoint)}
        >
          {profile?.coverPhotoId && (
            <div className="kiosk-pin-preview-image">
              <UralityImage imageId={profile?.coverPhotoId} size="lg" />
            </div>
          )}
          <div className="kiosk-pin-preview-body">
            {title && <h3 className="kiosk-pin-preview-title">{title}</h3>}
            {description && (
              <p className="kiosk-pin-preview-description">{description}</p>
            )}
          </div>
        </div>
        <div className="kiosk-pin-preview-nav-row">
          <button
            type="button"
            className="kiosk-pin-preview-nav clear"
            onClick={() => navigatePinPreview("prev")}
          >
            <span className="material-icons">keyboard_arrow_left</span>
          </button>
          <button
            type="button"
            className="kiosk-pin-preview-nav clear"
            onClick={() => navigatePinPreview("next")}
          >
            <span className="material-icons">keyboard_arrow_right</span>
          </button>
        </div>
      </div>
    );
  };

  const renderSelectedPinPanel = () => {
    if (!selectedPoint) return null;
    return (
      <div className="kiosk-pin-wrapper">
        <div className="kiosk-pin-header">
          <button
            type="button"
            className="kiosk-pin-preview-nav clear"
            onClick={onPrevPoint}
          >
            <span className="material-icons">keyboard_arrow_left</span>
          </button>
          <button
            type="button"
            className="kiosk-pin-preview-nav clear"
            onClick={onNextPoint}
          >
            <span className="material-icons">keyboard_arrow_right</span>
          </button>
          <div className="flex-grow" />
          <div className="clickable" onClick={() => setSelectedPoint(null)}>
            <span className="material-icons">close</span>
          </div>
        </div>
        <div className="kiosk-pin-content">
          <EventParticipantGroupParticipantView
            name={name}
            point={selectedPoint}
            onClick={null}
            condensed={false}
          />
        </div>
      </div>
    );
  };

  return (
    <div className="kiosk-map-container">
      <MapBoxMapWrapper
        height="100%"
        width="100%"
        locations={[]}
        routes={eventMap.hasRoutes ? mapRoutes : []}
        markers={kioskMarkers}
        overlays={amenityOverlays ?? null}
        defaultCenter={
          defaultCenter ?? eventMap.location ?? eventMap.owner?.location
        }
        defaultZoom={16}
        selectedLocation={selectedLocation}
        onMapClicked={() => {
          setPreviewPoint(null);
          setSelectedPoint(null);
          setSelectedLocation(null);
        }}
        interactive={true}
        addMyLocation={true}
        mapToggle={{
          pins: false,
          regions: false,
          routes: true,
          waypoints: false,
          assetGroups: false,
          features: false
        }}
      />
      {previewPoint && !selectedPoint && renderPinPreviewBar()}
      <div className={`kiosk-pin ${selectedPoint ? "show-content" : ""}`}>
        {renderSelectedPinPanel()}
      </div>
    </div>
  );
};

export default EventKioskMapView;
